import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { EntitlementType } from '@dreryk/drerykweb-medical-dictionaries-api';
import { AdditionalEntitlement, MedicinePrescription } from '@dreryk/egabinet-prescription-frontend-api';

import { PatientReimbursement, selectAutomaticSelectionAdditionalEntitlements } from '~libs.shared.prescription';
import { isEmpty } from '~libs.typescript';

import { DraftPrescriptionsFormController } from '../../draftPrescriptions/DraftPrescriptionsForm.types';

type AdditionalEntitlementsSelectorOptions = {
   initialAdditionalEntitlementTypes: EntitlementType[] | undefined;
   selectedPatientReimbursementByUser: PatientReimbursement | undefined;
   medicinePrescription: MedicinePrescription;
};

export const useAdditionalEntitlementsSelector = (
   options: AdditionalEntitlementsSelectorOptions
): AdditionalEntitlement[] => {
   return useMemo(() => {
      if (!isEmpty(options.initialAdditionalEntitlementTypes)) {
         return options.medicinePrescription.additionalEntitlements.filter((additionalEntitlement) =>
            options.initialAdditionalEntitlementTypes!.includes(additionalEntitlement.additionalEntitlementType)
         );
      }

      return selectAutomaticSelectionAdditionalEntitlements({
         additionalEntitlement: options.medicinePrescription.additionalEntitlements,
         reimbursement: options.selectedPatientReimbursementByUser,
      });
   }, [
      options.medicinePrescription,
      options.initialAdditionalEntitlementTypes,
      options.selectedPatientReimbursementByUser,
   ]);
};

export const useSelectedAdditionalEntitlementsSync = (
   medicinePrescription: MedicinePrescription,
   selectedEntitlements: AdditionalEntitlement[],
   setValue: DraftPrescriptionsFormController['setValue'],
   fieldName: string
): void => {
   useUpdateEffect(() => {
      const entitlementsToUpdate = selectedEntitlements.map((entitlement) => {
         return (
            medicinePrescription.additionalEntitlements.find(
               (prescriptionEntitlement) =>
                  prescriptionEntitlement.additionalEntitlementType === entitlement.additionalEntitlementType
            ) ?? entitlement
         );
      });
      setValue(fieldName, entitlementsToUpdate);
   }, [medicinePrescription]);
};
